import React, { useMemo } from 'react';
import { Box, Typography, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import GridContainer from '@/components/Grid/GridContainer';
import GridItem from '@/components/Grid/GridItem';
import { useStyles } from '@/styles/templateInputStyles';
import { useStylesDanger } from '@/styles/useStylesDanger';
import {
  buildLineItemHistoryRows,
  sanitizeLineItemsForHistoryCompare,
} from '@/utils/lineItemHistory';
import { isFieldVisible } from '@/utils/fieldConfig';

const labelStyle = {
  fontSize: '12px',
  color: 'grey',
};

const HISTORY_FIELDS = [
  { key: 'gl_code_1', labelKey: 'lineItems.fields.glCode1', md: 4 },
  { key: 'gl_code_2', labelKey: 'lineItems.fields.glCode2', md: 4 },
  { key: 'gl_code_3', labelKey: 'lineItems.fields.glCode3', md: 4 },
  { key: 'gl_code_4', labelKey: 'lineItems.fields.glCode4', md: 4 },
  { key: 'gl_code_5', labelKey: 'lineItems.fields.glCode5', md: 4 },
  { key: 'amount', labelKey: 'lineItems.fields.amount', md: 4 },
  { key: 'identifier', labelKey: 'lineItems.fields.identifier', md: 4 },
  { key: 'memo', labelKey: 'lineItems.fields.memo', md: 4 },
  {
    key: 'internal_comment',
    labelKey: 'lineItems.fields.internalComment',
    md: 12,
  },
];

const statusColor = {
  added: '#3AB795',
  removed: '#d32f2f',
  changed: '#ed6c02',
};

const isHistoryFieldVisible = (
  fieldKey,
  refundModalType,
  customPaymentType,
) => {
  // Without CPT config, keep the previous default field set (no GL 4/5).
  // When config is available, match the edit form visibility rules.
  if (!customPaymentType) {
    return fieldKey !== 'gl_code_4' && fieldKey !== 'gl_code_5';
  }
  return isFieldVisible(
    `lineItem.${fieldKey}`,
    refundModalType,
    customPaymentType,
  );
};

const LineItemsHistorySection = ({
  lineItems,
  previousLineItems,
  customPaymentType,
  refundModalType,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const dangerClass = useStylesDanger();

  const visibleFields = useMemo(
    () =>
      HISTORY_FIELDS.filter((field) =>
        isHistoryFieldVisible(field.key, refundModalType, customPaymentType),
      ),
    [customPaymentType, refundModalType],
  );

  const rows = useMemo(() => {
    const visibleFieldKeys = visibleFields.map((field) => field.key);
    const sanitizedLineItems = sanitizeLineItemsForHistoryCompare(
      lineItems,
      visibleFieldKeys,
    );
    const sanitizedPreviousLineItems =
      previousLineItems == null
        ? null
        : sanitizeLineItemsForHistoryCompare(
            previousLineItems,
            visibleFieldKeys,
          );

    return buildLineItemHistoryRows(
      sanitizedLineItems,
      sanitizedPreviousLineItems,
    );
  }, [lineItems, previousLineItems, visibleFields]);

  if (!rows.length) {
    return null;
  }

  return (
    <GridItem style={{ marginTop: '20px' }} xs={12} sm={12} md={12}>
      <Typography
        variant='subtitle1'
        sx={{ fontWeight: 'bold', mb: 1, fontSize: '14px' }}>
        {t('lineItems.title')}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {rows.map((row, index) => {
          const statusLabel =
            row.status === 'added'
              ? t('refundInfoHistory.lineItems.added')
              : row.status === 'removed'
              ? t('refundInfoHistory.lineItems.removed')
              : row.status === 'changed'
              ? t('refundInfoHistory.lineItems.changed')
              : null;

          return (
            <Box
              key={`${row.status}-${index}`}
              sx={{
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                p: 2,
                backgroundColor: '#fafafa',
                opacity: row.status === 'removed' ? 0.75 : 1,
              }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: 1.5,
                  flexWrap: 'wrap',
                }}>
                <Typography variant='subtitle2' sx={{ fontWeight: 'bold' }}>
                  {t('lineItems.itemLabel', { number: index + 1 })}
                </Typography>
                {statusLabel ? (
                  <Typography
                    component='span'
                    sx={{
                      fontSize: '12px',
                      color: '#fff',
                      background: statusColor[row.status],
                      borderRadius: '4px',
                      px: 1,
                      py: 0.25,
                    }}>
                    {statusLabel}
                  </Typography>
                ) : null}
              </Box>
              <GridContainer>
                {visibleFields.map((field) => {
                  const isChanged = row.changedFields.has(field.key);
                  return (
                    <GridItem
                      key={field.key}
                      xs={12}
                      sm={field.md === 12 ? 12 : 6}
                      md={field.md}>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          width: '100%',
                        }}>
                        <label style={labelStyle}>{t(field.labelKey)}</label>
                        <TextField
                          variant='outlined'
                          className={
                            isChanged ? dangerClass.root : classes.root
                          }
                          multiline
                          inputProps={{
                            style: {
                              fontSize: '14px',
                              lineHeight: '1.3',
                              width: '100%',
                              textDecoration:
                                row.status === 'removed'
                                  ? 'line-through'
                                  : 'none',
                            },
                          }}
                          minRows={1}
                          maxRows={3}
                          value={row.item[field.key] ?? ''}
                          onChange={() => null}
                          name={`history_${field.key}_${index}`}
                        />
                      </div>
                    </GridItem>
                  );
                })}
              </GridContainer>
            </Box>
          );
        })}
      </Box>
    </GridItem>
  );
};

export default LineItemsHistorySection;
